/**
 * Teklif tutar kırılımı, ekran kopyası (Ürün Sahibi, 09.10.2026): ürün fiyatı (KDV hariç net), ek ücret, indirim,
 * KDV (oran başına), devlet harcı, yuvarlama, genel toplam. Sunucudaki `api/src/common/workflow/quote-breakdown.ts`
 * ile aynı algoritma; iki dosya aynı sabit örneklerle test edilir. Fiyat hesaplamaz: donmuş (ya da taslakta
 * hesaplanmış) değerleri sunum sırasına dizer. Dahil kalemde net = brüt - KDV, ayrı yuvarlama yok.
 */
export interface KirilimKalem {
  quantity: number; unitPriceMinor: number; feeMinor: number; discountMinor: number;
  governmentFeeMinor?: number | null; taxMinor?: number | null; taxMode?: string | null; taxRateBps?: number | null; vatMinor?: number | null;
}
export interface KirilimSurum {
  taxMode?: string | null; taxRateBps?: number | null; taxMinor?: number | null; discountMinor: number; totalMinor: number;
  roundingAdjustmentMinor?: number | null; items: readonly KirilimKalem[];
}
export interface TutarKirilimi {
  urunMinor: number; ucretMinor: number; indirimMinor: number; kdvSatirlari: { rateBps: number; taxMinor: number }[];
  eskiVergiMinor: number; devletHarciMinor: number; yuvarlamaMinor: number; toplamMinor: number; eski: boolean;
}
const KIPLER = new Set(['NONE', 'EXCLUSIVE', 'INCLUSIVE']);

export function buildTutarKirilimi(version: KirilimSurum): TutarKirilimi {
  const surumKipi = version.taxMode && KIPLER.has(version.taxMode) ? version.taxMode : null;
  const surumOrani = version.taxRateBps ?? 0;
  const sum = (pick: (item: KirilimKalem) => number) => version.items.reduce((total, item) => total + pick(item), 0);
  const brut = sum((i) => i.quantity * i.unitPriceMinor);
  const devlet = sum((i) => i.governmentFeeMinor ?? 0);
  const orana = new Map<number, number>();
  let dahilKdv = 0;
  if (surumKipi !== null) {
    const kalemBazli = version.items.some((i) => (i.taxMode ?? null) !== null || (i.taxRateBps ?? null) !== null);
    if (!kalemBazli) {
      if (surumKipi !== 'NONE' && surumOrani > 0 && (version.taxMinor ?? 0) > 0) orana.set(surumOrani, version.taxMinor ?? 0);
      if (surumKipi === 'INCLUSIVE') dahilKdv = version.taxMinor ?? 0;
    } else {
      for (const item of version.items) {
        const kip = item.taxMode && KIPLER.has(item.taxMode) ? item.taxMode : surumKipi;
        const oran = item.taxRateBps ?? surumOrani;
        const kdv = item.vatMinor ?? 0;
        // Vergisiz ve oranı 0 kalemin KDV'si zaten 0 donmuştur (satır üretmez); ayrı bir kontrol gerekmez.
        if (kdv > 0) orana.set(oran, (orana.get(oran) ?? 0) + kdv);
        if (kip === 'INCLUSIVE') dahilKdv += kdv;
      }
    }
  }
  return {
    urunMinor: brut - devlet - dahilKdv,
    ucretMinor: sum((i) => i.feeMinor),
    indirimMinor: sum((i) => i.discountMinor) + version.discountMinor,
    kdvSatirlari: [...orana.entries()].sort((a, b) => a[0] - b[0]).map(([rateBps, taxMinor]) => ({ rateBps, taxMinor })),
    eskiVergiMinor: surumKipi === null ? sum((i) => i.taxMinor ?? 0) : 0,
    devletHarciMinor: devlet,
    yuvarlamaMinor: version.roundingAdjustmentMinor ?? 0,
    toplamMinor: version.totalMinor,
    eski: surumKipi === null,
  };
}

export function kirilimFarki(k: TutarKirilimi): number {
  const kdv = k.kdvSatirlari.reduce((total, satir) => total + satir.taxMinor, 0);
  return k.urunMinor + k.ucretMinor - k.indirimMinor + kdv + k.eskiVergiMinor + k.devletHarciMinor + k.yuvarlamaMinor - k.toplamMinor;
}

export interface KirilimSatiri { tur: 'urun' | 'ucret' | 'indirim' | 'kdv' | 'eskiVergi' | 'harc' | 'yuvarlama' | 'toplam'; etiket: string; minor: number }
export interface KirilimMetni {
  urun: string; urunKdvHaric: string; ucret: string; indirim: string; kdv: (oranYuzde: string) => string; eskiVergi: string;
  harc: string; yuvarlama: string; toplam: string; toplamKdvDahil: string; toplamKdvYok: string;
}

/** Satır sırası ve etiket seçimi sunucudaki `kirilimSatirlari` ile aynı; sıfır satır gösterilmez. */
export function kirilimSatirlari(k: TutarKirilimi, m: KirilimMetni): KirilimSatiri[] {
  const kdvVar = k.kdvSatirlari.length > 0;
  const satirlar: KirilimSatiri[] = [{ tur: 'urun', etiket: kdvVar ? m.urunKdvHaric : m.urun, minor: k.urunMinor }];
  if (k.ucretMinor > 0) satirlar.push({ tur: 'ucret', etiket: m.ucret, minor: k.ucretMinor });
  if (k.indirimMinor > 0) satirlar.push({ tur: 'indirim', etiket: m.indirim, minor: -k.indirimMinor });
  for (const kdv of k.kdvSatirlari) satirlar.push({ tur: 'kdv', etiket: m.kdv(String(kdv.rateBps / 100)), minor: kdv.taxMinor });
  if (k.eskiVergiMinor > 0) satirlar.push({ tur: 'eskiVergi', etiket: m.eskiVergi, minor: k.eskiVergiMinor });
  if (k.devletHarciMinor > 0) satirlar.push({ tur: 'harc', etiket: m.harc, minor: k.devletHarciMinor });
  if (k.yuvarlamaMinor !== 0) satirlar.push({ tur: 'yuvarlama', etiket: m.yuvarlama, minor: k.yuvarlamaMinor });
  satirlar.push({ tur: 'toplam', etiket: kdvVar ? m.toplamKdvDahil : k.eski ? m.toplam : m.toplamKdvYok, minor: k.toplamMinor });
  return satirlar;
}

/** Kayıtlı teklif kalemini (API) kırılım girdisine çevirir; boş sayısal alanlar 0 sayılır. */
export function kalemKirilimi(item: { quantity: number; unitPriceMinor: number; feeMinor?: number; discountMinor?: number; governmentFeeMinor?: number; taxMinor?: number; taxMode?: string | null; taxRateBps?: number | null; vatMinor?: number | null }): KirilimKalem {
  return { quantity: item.quantity, unitPriceMinor: item.unitPriceMinor, feeMinor: item.feeMinor ?? 0, discountMinor: item.discountMinor ?? 0, governmentFeeMinor: item.governmentFeeMinor ?? 0, taxMinor: item.taxMinor ?? 0, taxMode: item.taxMode ?? null, taxRateBps: item.taxRateBps ?? null, vatMinor: item.vatMinor ?? null };
}

/**
 * Taslak (henüz kaydedilmemiş) teklifin kırılımı: `priceQuoteDraft` çıktısından. Taslakta yuvarlama gösterilmez
 * (sunucu kaydederken uygular). Toplam, hesaplanmış taslak toplamıdır.
 */
export function taslakKirilimi(priced: {
  items: { quantity: number; unitPriceMinor: number; feeMinor?: number; discountMinor?: number; governmentFeeMinor?: number; taxMinor?: number; taxMode?: string; taxRateBps?: number }[];
  discountMinor: number;
  tax?: { taxMode: string; taxRateBps: number; taxMinor: number; totalMinor: number; lines?: { taxMinor: number }[] };
}): TutarKirilimi {
  const eskiToplam = priced.items.reduce((total, item) => total + item.quantity * item.unitPriceMinor + (item.taxMinor ?? 0) + (item.feeMinor ?? 0) - (item.discountMinor ?? 0), 0) - priced.discountMinor;
  return buildTutarKirilimi({
    taxMode: priced.tax?.taxMode ?? null, taxRateBps: priced.tax?.taxRateBps ?? null, taxMinor: priced.tax?.taxMinor ?? null,
    discountMinor: priced.discountMinor, totalMinor: priced.tax ? priced.tax.totalMinor : eskiToplam, roundingAdjustmentMinor: 0,
    items: priced.items.map((item, index) => ({ ...kalemKirilimi(item), vatMinor: priced.tax?.lines?.[index]?.taxMinor ?? null })),
  });
}
